/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { capacityHeaderControlPanelEntryPoint } from '@jira/spa-apps__software-capacity-header-control-panel/src/capacityHeaderControlPanelEntryPoint.entrypoint.tsx';
import type { TenantContext } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import { capacityEntryPoint } from '@jira/spa-apps__software-capacity-people/src/capacityEntryPoint.entrypoint.tsx';
import { capacityWorkEntryPoint } from '@jira/spa-apps__software-capacity-work/src/capacityWorkEntryPoint.entrypoint.tsx';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import parameters from './__generated__/SoftwareCapacityContainerBoardFilterQuery$parameters';
import type { SoftwareCapacityContainerBoardFilterQuery } from './__generated__/SoftwareCapacityContainerBoardFilterQuery.graphql';

const ICP_MOST_RECENT_VIEW_COOKIE = 'icp_most_recent_view';

declare global {
	// oxlint-disable-next-line vars-on-top
	var __GET_COOKIE__: ((name?: string) => string | undefined) | undefined;
}

type CapacityView = 'people' | 'work';

const isCapacityView = (value: unknown): value is CapacityView =>
	value === 'people' || value === 'work';

const getCookieValue = (name: string) =>
	globalThis?.__GET_COOKIE__?.(name) ??
	globalThis?.document?.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`)?.pop();

const getViewFromCookie = (): CapacityView => {
	const mostRecentView = getCookieValue(ICP_MOST_RECENT_VIEW_COOKIE)?.toLowerCase();
	return mostRecentView === 'work' ? 'work' : 'people';
};

type CapacityExtraProps = {
	activeView: CapacityView;
	viewParam: CapacityView | undefined;
	tenantContext: TenantContext;
};

type CapacityEntryPoints = {
	headerControlPanel?: typeof capacityHeaderControlPanelEntryPoint;
	people?: typeof capacityEntryPoint;
	work?: typeof capacityWorkEntryPoint;
};

type CapacityQueries = {
	boardFilterData: SoftwareCapacityContainerBoardFilterQuery;
};

type SoftwareCapacityParentEntryPointComponent = EntryPointComponent<
	CapacityQueries,
	CapacityEntryPoints,
	{},
	CapacityExtraProps
>;

export const softwareCapacityParentEntryPoint: EntryPoint<
	SoftwareCapacityParentEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<SoftwareCapacityParentEntryPointComponent>(
		/* webpackChunkName: "async-jira-software-capacity" */ './SoftwareCapacityContainer.tsx',
	),
	getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
		const {
			context: { match, route },
			tenantContext,
		} = entryPointParams;
		const viewParamRaw = match.params.view;
		const viewParam = isCapacityView(viewParamRaw) ? viewParamRaw : undefined;
		const boardId = match.params.boardId;
		if (!boardId) {
			throw new Error('boardId is required for software capacity');
		}

		const boardAri = `ari:cloud:jira-software:${tenantContext.cloudId}:board/${boardId}`;
		const isCMP = route.name.startsWith('software-capacity-classic');

		// Use URL param if set, otherwise fall back to cookie.
		const activeView: CapacityView = viewParam ?? getViewFromCookie();

		return {
			queries: {
				boardFilterData: {
					options: {
						fetchPolicy: 'store-or-network' as const,
					},
					parameters,
					variables: {
						boardId: boardAri,
						isCMP,
					},
				},
			},
			extraProps: {
				activeView,
				viewParam,
				tenantContext,
			},
			entryPoints: {
				headerControlPanel: {
					entryPoint: capacityHeaderControlPanelEntryPoint,
					entryPointParams,
				},
				work:
					activeView === 'work'
						? {
								entryPoint: capacityWorkEntryPoint,
								entryPointParams,
							}
						: undefined,
				people:
					activeView === 'people'
						? {
								entryPoint: capacityEntryPoint,
								entryPointParams,
							}
						: undefined,
			},
		};
	},
});
